import React, { useState, useCallback, useEffect } from 'react';
import texasApi from '../../services/texasApi';

// ── Device registry (mirrors backend SUBCHAPTER_D_DEVICES) ─────────────────
const DEVICE_TYPES = [
  { key: 'windowLatches',        label: 'Window Latches',              statutory: '§ 92.153(a)(1)' },
  { key: 'keyedDeadbolts',       label: 'Keyed Deadbolts',             statutory: '§ 92.153(a)(2)' },
  { key: 'keylessBoltingDevices',label: 'Keyless Bolting Devices',     statutory: '§ 92.153(a)(3)' },
  { key: 'doorViewers',          label: 'Door Viewers (Peepholes)',     statutory: '§ 92.153(a)(4)' },
  { key: 'slidingDoorPinLocks',  label: 'Sliding Door Pin Locks',      statutory: '§ 92.153(a)(5)' },
  { key: 'slidingDoorSecurityBars', label: 'Sliding Door Security Bars', statutory: '§ 92.153(a)(6)' },
];

// ── Types ───────────────────────────────────────────────────────────────────
interface DeviceState {
  key: string;
  installed: boolean;
  photoEvidenceUrl: string;
  notes: string;
}

interface BackendDevice {
  key: string;
  installed: boolean;
  photoEvidenceId?: string;
  notes?: string;
  [key: string]: unknown;
}

interface CureStatus {
  cureWindowActive: boolean;
  cureWindowStartedAt: string | null;
  expiresAt: string | null;
  daysRemaining: number | null;
  cureFailed: boolean;
  terminationNoticeRequired: boolean;
  terminationNoticeIssued: boolean;
  allDevicesInstalled: boolean;
  certified: boolean;
}

type Tab = 'checklist' | 'certify' | 'reinspect' | 'termination';

// ── Helpers ─────────────────────────────────────────────────────────────────
function initialDeviceStates(): DeviceState[] {
  return DEVICE_TYPES.map((d) => ({
    key: d.key,
    installed: false,
    photoEvidenceUrl: '',
    notes: '',
  }));
}

function mergeFromBackend(backend: BackendDevice[]): DeviceState[] {
  return DEVICE_TYPES.map((d) => {
    const found = backend.find((b) => b.key === d.key);
    return {
      key: d.key,
      installed: found ? !!found.installed : false,
      photoEvidenceUrl: (found?.photoEvidenceId as string) || '',
      notes: (found?.notes as string) || '',
    };
  });
}

function progressPct(devices: DeviceState[]): number {
  const installed = devices.filter((d) => d.installed).length;
  return Math.round((installed / devices.length) * 100);
}

// ══════════════════════════════════════════════════════════════════════════════
// SubchapterDChecklist — F-153, US-114, T-114-001/002/003
// ══════════════════════════════════════════════════════════════════════════════
export const SubchapterDChecklist: React.FC = () => {
  const [propertyId, setPropertyId]     = useState('');
  const [activeTab, setActiveTab]       = useState<Tab>('checklist');
  const [devices, setDevices]           = useState<DeviceState[]>(initialDeviceStates());
  const [checklistLoaded, setChecklistLoaded] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess]   = useState<string | null>(null);

  // Certify tab
  const [certifierName, setCertifierName] = useState('');
  const [certifierDate, setCertifierDate] = useState('');
  const [oathSigned, setOathSigned]       = useState(false);
  const [certifying, setCertifying]       = useState(false);
  const [certifyMsg, setCertifyMsg]       = useState<string | null>(null);

  // Re-inspect tab
  const [reinspectDevice, setReinspectDevice] = useState('');
  const [reinspectCompleteDevice, setReinspectCompleteDevice] = useState('');
  const [reinspecting, setReinspecting]   = useState(false);
  const [reinspectMsg, setReinspectMsg]   = useState<string | null>(null);

  // Termination tab
  const [tenantId, setTenantId]           = useState('');
  const [cureStatus, setCureStatus]       = useState<CureStatus | null>(null);
  const [issuing, setIssuing]             = useState(false);
  const [terminationMsg, setTerminationMsg] = useState<string | null>(null);

  // ── Load checklist from backend ─────────────────────────────────────────
  const loadChecklist = useCallback(async (pid: string) => {
    if (!pid) return;
    setLoading(true);
    setError(null);
    setSaveSuccess(null);
    try {
      const data = await texasApi.hardware.getChecklist(pid);
      const backendDevices = (data?.devices as BackendDevice[]) || [];
      setDevices(mergeFromBackend(backendDevices));
      setChecklistLoaded(true);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        // No checklist yet — start fresh
        setDevices(initialDeviceStates());
        setChecklistLoaded(true);
      } else if (status === 401) {
        setError('Authentication required. Please log in and try again.');
      } else if (status === 403) {
        setError('Access denied. You do not have permission to view this checklist.');
      } else {
        setError(err?.response?.data?.message || err?.message || 'Failed to load checklist.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCureStatus = useCallback(async (pid: string) => {
    if (!pid) return;
    try {
      const res = await texasApi.hardware.getCureStatus(pid);
      const d = (res as any)?.data || (res as any);
      setCureStatus(d);
    } catch {
      setCureStatus(null);
    }
  }, []);

  const handleSearch = () => {
    if (propertyId.trim()) loadChecklist(propertyId.trim());
  };

  // Reload cure status whenever termination tab is opened
  useEffect(() => {
    if (activeTab === 'termination' && propertyId.trim()) {
      loadCureStatus(propertyId.trim());
    }
  }, [activeTab, propertyId, loadCureStatus]);

  // ── T-114-001: per-device toggle ─────────────────────────────────────────
  const handleDeviceToggle = (key: string) => {
    setDevices((prev) =>
      prev.map((d) => (d.key === key ? { ...d, installed: !d.installed } : d))
    );
    setSaveSuccess(null);
  };

  const handlePhotoUrl = (key: string, val: string) => {
    setDevices((prev) =>
      prev.map((d) => (d.key === key ? { ...d, photoEvidenceUrl: val } : d))
    );
  };

  const handleNotes = (key: string, val: string) => {
    setDevices((prev) =>
      prev.map((d) => (d.key === key ? { ...d, notes: val } : d))
    );
  };

  const handleSaveChecklist = async () => {
    if (!propertyId.trim()) return;
    setSaving(true);
    setError(null);
    setSaveSuccess(null);
    try {
      const devicesPayload: Record<string, unknown> = {};
      for (const d of devices) {
        devicesPayload[d.key] = {
          installed: d.installed,
          photoEvidenceId: d.photoEvidenceUrl || null,
          notes: d.notes || null,
        };
      }
      await texasApi.hardware.createChecklist({ propertyId: propertyId.trim(), devices: devicesPayload });
      setSaveSuccess('Checklist saved successfully.');
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save checklist.');
    } finally {
      setSaving(false);
    }
  };

  const pct = progressPct(devices);
  const allInstalled = devices.every((d) => d.installed);
  const failedDevices = devices.filter((d) => !d.installed);

  // ── T-114-002: certify with oath ─────────────────────────────────────────
  const handleCertify = async () => {
    if (!propertyId.trim() || !certifierName.trim() || !certifierDate || !oathSigned) return;
    setCertifying(true);
    setCertifyMsg(null);
    setError(null);
    try {
      await texasApi.hardware.certify({
        propertyId: propertyId.trim(),
        landlordId: certifierName.trim(),
        oathStatement: `I, ${certifierName.trim()}, certify under penalty of perjury that all six Subchapter D security devices required by Texas Property Code § 92.153 have been installed in the above property as of ${certifierDate}.`,
      });
      setCertifyMsg('✅ Certification recorded. This record is timestamped and immutable.');
      await loadChecklist(propertyId.trim());
    } catch (err: any) {
      setCertifyMsg(`❌ ${err?.response?.data?.message || err?.message || 'Certification failed.'}`);
    } finally {
      setCertifying(false);
    }
  };

  // ── T-114-003: re-inspection scheduling ──────────────────────────────────
  const handleReinspect = async () => {
    if (!propertyId.trim() || !reinspectDevice) return;
    setReinspecting(true);
    setReinspectMsg(null);
    try {
      await texasApi.hardware.reinspect({ propertyId: propertyId.trim(), deviceKey: reinspectDevice });
      setReinspectMsg(`✅ Re-inspection scheduled for "${DEVICE_TYPES.find(d => d.key === reinspectDevice)?.label}". 7-day cure window is active.`);
    } catch (err: any) {
      setReinspectMsg(`❌ ${err?.response?.data?.message || err?.message || 'Failed to schedule re-inspection.'}`);
    } finally {
      setReinspecting(false);
    }
  };

  const handleCompleteReinspection = async () => {
    if (!propertyId.trim() || !reinspectCompleteDevice) return;
    setReinspecting(true);
    setReinspectMsg(null);
    try {
      await texasApi.hardware.completeReinspection(propertyId.trim(), reinspectCompleteDevice);
      setReinspectMsg(`✅ Re-inspection completed for "${DEVICE_TYPES.find(d => d.key === reinspectCompleteDevice)?.label}". Device marked passed.`);
      await loadChecklist(propertyId.trim());
    } catch (err: any) {
      setReinspectMsg(`❌ ${err?.response?.data?.message || err?.message || 'Failed to complete re-inspection.'}`);
    } finally {
      setReinspecting(false);
    }
  };

  // ── T-114-003: tenant termination workflow ────────────────────────────────
  const handleIssueTermination = async () => {
    if (!propertyId.trim() || !tenantId.trim()) return;
    setIssuing(true);
    setTerminationMsg(null);
    try {
      await texasApi.hardware.issueTermination({ propertyId: propertyId.trim(), tenantId: tenantId.trim() });
      setTerminationMsg('✅ Tenant right-to-terminate notice issued per Texas Property Code § 92.156. Tenant may terminate the lease without penalty.');
      await loadCureStatus(propertyId.trim());
    } catch (err: any) {
      setTerminationMsg(`❌ ${err?.response?.data?.message || err?.message || 'Failed to issue termination notice.'}`);
    } finally {
      setIssuing(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div data-testid="subchapter-d-checklist" className="texas-page-container">
      <h1>Subchapter D Compliance Checklist</h1>
      <p className="page-description">
        Texas Property Code § 92.153 — Six mandated security devices per dwelling unit.
        Landlords must provide and maintain all six; failure triggers a 7-day cure window (§ 92.156).
      </p>

      {/* Property search */}
      <div className="search-bar" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Enter property ID"
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value)}
          data-testid="property-id-input"
          style={{ flex: 1 }}
        />
        <button onClick={handleSearch} disabled={loading} data-testid="search-btn">
          {loading ? 'Loading…' : 'Load Checklist'}
        </button>
      </div>

      {error && (
        <div className="error-callout" data-testid="error-callout" role="alert" style={{ marginBottom: 12, padding: 12, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6 }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {checklistLoaded && (
        <>
          {/* Tab navigation */}
          <div className="tab-bar" style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
            {(['checklist', 'certify', 'reinspect', 'termination'] as Tab[]).map((tab) => (
              <button
                key={tab}
                data-testid={`tab-${tab}`}
                onClick={() => setActiveTab(tab)}
                style={{ fontWeight: activeTab === tab ? 700 : 400, padding: '6px 14px', borderRadius: 4,
                  background: activeTab === tab ? '#1d4ed8' : '#e5e7eb', color: activeTab === tab ? '#fff' : '#111' }}
              >
                {tab === 'checklist' ? 'Device Checklist' : tab === 'certify' ? 'Certify' : tab === 'reinspect' ? 'Re-Inspection' : 'Termination Workflow'}
              </button>
            ))}
          </div>

          {/* ── TAB 1: Device Checklist (T-114-001) ─────────────────────── */}
          {activeTab === 'checklist' && (
            <div data-testid="tab-panel-checklist">
              {/* Progress bar */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span>Installation progress</span>
                  <span data-testid="progress-pct">{pct}%</span>
                </div>
                <div data-testid="progress-bar" style={{ background: '#e5e7eb', borderRadius: 4, height: 10 }}>
                  <div style={{ width: `${pct}%`, background: pct === 100 ? '#16a34a' : '#2563eb', height: '100%', borderRadius: 4, transition: 'width 0.2s' }} />
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  {devices.filter(d => d.installed).length} / {devices.length} devices installed
                </div>
              </div>

              {saveSuccess && (
                <div data-testid="save-success" style={{ marginBottom: 8, padding: 8, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, color: '#15803d' }}>
                  {saveSuccess}
                </div>
              )}

              <table className="device-table" data-testid="device-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    <th style={{ textAlign: 'left', padding: '8px 6px' }}>Device</th>
                    <th style={{ textAlign: 'left', padding: '8px 6px' }}>Statutory Reference</th>
                    <th style={{ textAlign: 'center', padding: '8px 6px' }}>Installed</th>
                    <th style={{ textAlign: 'left', padding: '8px 6px' }}>Photo Evidence URL</th>
                    <th style={{ textAlign: 'left', padding: '8px 6px' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {DEVICE_TYPES.map((dt) => {
                    const ds = devices.find((d) => d.key === dt.key)!;
                    return (
                      <tr key={dt.key} data-testid={`device-row-${dt.key}`} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '8px 6px', fontWeight: 500 }}>{dt.label}</td>
                        <td style={{ padding: '8px 6px', fontSize: 12, color: '#6b7280' }}>{dt.statutory}</td>
                        <td style={{ padding: '8px 6px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            data-testid={`toggle-${dt.key}`}
                            checked={ds.installed}
                            onChange={() => handleDeviceToggle(dt.key)}
                            aria-label={`Toggle ${dt.label} installed`}
                          />
                        </td>
                        <td style={{ padding: '8px 6px' }}>
                          <input
                            type="text"
                            data-testid={`photo-${dt.key}`}
                            placeholder="Photo URL or evidence ID"
                            value={ds.photoEvidenceUrl}
                            onChange={(e) => handlePhotoUrl(dt.key, e.target.value)}
                            style={{ width: '100%', fontSize: 13 }}
                          />
                        </td>
                        <td style={{ padding: '8px 6px' }}>
                          <input
                            type="text"
                            data-testid={`notes-${dt.key}`}
                            placeholder="Notes"
                            value={ds.notes}
                            onChange={(e) => handleNotes(dt.key, e.target.value)}
                            style={{ width: '100%', fontSize: 13 }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <button
                data-testid="save-checklist-btn"
                onClick={handleSaveChecklist}
                disabled={saving}
                style={{ marginTop: 12, padding: '8px 20px', background: '#2563eb', color: '#fff', borderRadius: 4 }}
              >
                {saving ? 'Saving…' : 'Save Checklist'}
              </button>
            </div>
          )}

          {/* ── TAB 2: Certify with oath (T-114-002) ────────────────────── */}
          {activeTab === 'certify' && (
            <div data-testid="tab-panel-certify">
              {allInstalled ? (
                <>
                  <p style={{ marginBottom: 12, color: '#15803d', fontWeight: 500 }}>
                    ✅ All 6 devices are marked installed. Complete the certification below.
                  </p>

                  {/* Certifier fields */}
                  <div style={{ marginBottom: 12 }}>
                    <label htmlFor="certifier-name" style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                      Certifier name (landlord / agent)
                    </label>
                    <input
                      id="certifier-name"
                      type="text"
                      data-testid="certifier-name"
                      value={certifierName}
                      onChange={(e) => setCertifierName(e.target.value)}
                      placeholder="Full legal name"
                      style={{ width: '100%', padding: 8 }}
                    />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label htmlFor="certifier-date" style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                      Certification date
                    </label>
                    <input
                      id="certifier-date"
                      type="date"
                      data-testid="certifier-date"
                      value={certifierDate}
                      onChange={(e) => setCertifierDate(e.target.value)}
                      style={{ padding: 8 }}
                    />
                  </div>

                  {/* Oath text — displayed clearly per T-114-002 */}
                  <div data-testid="oath-text" style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 6, padding: 14, marginBottom: 14 }}>
                    <p style={{ fontWeight: 700, marginBottom: 8 }}>Oath of Landlord Certification</p>
                    <p style={{ fontStyle: 'italic', fontSize: 14, lineHeight: 1.6 }}>
                      I, <strong>{certifierName || '[Certifier Name]'}</strong>, hereby certify <strong>under penalty of perjury</strong> that all six (6) security devices required by Texas Property Code § 92.153 — window latches, keyed deadbolts, keyless bolting devices, door viewers, sliding door pin locks, and sliding door security bars — have been installed in the above-referenced property as of <strong>{certifierDate || '[Date]'}</strong>. This certification is made for the purposes of compliance with Texas Property Code Chapter 92, Subchapter D.
                    </p>
                  </div>

                  {/* Digital signature / acknowledgement */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                    <input
                      type="checkbox"
                      id="oath-signed"
                      data-testid="oath-signed-checkbox"
                      checked={oathSigned}
                      onChange={(e) => setOathSigned(e.target.checked)}
                    />
                    <label htmlFor="oath-signed" style={{ fontWeight: 500 }}>
                      I digitally sign and agree to the above oath
                    </label>
                  </div>

                  <button
                    data-testid="certify-btn"
                    onClick={handleCertify}
                    disabled={certifying || !certifierName.trim() || !certifierDate || !oathSigned}
                    style={{ padding: '8px 20px', background: '#15803d', color: '#fff', borderRadius: 4 }}
                  >
                    {certifying ? 'Recording Certification…' : 'Record Certification'}
                  </button>
                </>
              ) : (
                <div data-testid="certify-blocked" style={{ padding: 12, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6 }}>
                  <p style={{ fontWeight: 600, marginBottom: 6 }}>Certification blocked</p>
                  <p>The following devices are not yet marked installed:</p>
                  <ul style={{ marginTop: 6 }}>
                    {failedDevices.map((d) => (
                      <li key={d.key}>{DEVICE_TYPES.find(dt => dt.key === d.key)?.label}</li>
                    ))}
                  </ul>
                  <p style={{ marginTop: 8, fontSize: 13 }}>
                    Return to the Device Checklist tab, mark all devices installed, save the checklist, then return here to certify.
                  </p>
                </div>
              )}

              {certifyMsg && (
                <div data-testid="certify-msg" style={{ marginTop: 12, padding: 10, borderRadius: 6,
                  background: certifyMsg.startsWith('✅') ? '#f0fdf4' : '#fef2f2' }}>
                  {certifyMsg}
                </div>
              )}
            </div>
          )}

          {/* ── TAB 3: Re-inspection (T-114-003) ────────────────────────── */}
          {activeTab === 'reinspect' && (
            <div data-testid="tab-panel-reinspect">
              <p style={{ marginBottom: 12 }}>
                Schedule a re-inspection for a failed device (triggers 7-day cure window under § 92.156),
                or mark a re-inspection complete once the device passes.
              </p>

              {/* Schedule re-inspection */}
              <div style={{ marginBottom: 20, padding: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                <h3 style={{ marginBottom: 10 }}>Schedule Re-Inspection</h3>
                <select
                  data-testid="reinspect-device-select"
                  value={reinspectDevice}
                  onChange={(e) => setReinspectDevice(e.target.value)}
                  style={{ padding: 8, marginBottom: 10, width: '100%' }}
                >
                  <option value="">— Select failed device —</option>
                  {failedDevices.map((d) => (
                    <option key={d.key} value={d.key}>
                      {DEVICE_TYPES.find(dt => dt.key === d.key)?.label}
                    </option>
                  ))}
                </select>
                <button
                  data-testid="reinspect-btn"
                  onClick={handleReinspect}
                  disabled={reinspecting || !reinspectDevice}
                  style={{ padding: '8px 16px', background: '#d97706', color: '#fff', borderRadius: 4 }}
                >
                  {reinspecting ? 'Scheduling…' : 'Schedule Re-Inspection'}
                </button>
              </div>

              {/* Complete re-inspection */}
              <div style={{ marginBottom: 20, padding: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                <h3 style={{ marginBottom: 10 }}>Mark Re-Inspection Complete</h3>
                <select
                  data-testid="reinspect-complete-select"
                  value={reinspectCompleteDevice}
                  onChange={(e) => setReinspectCompleteDevice(e.target.value)}
                  style={{ padding: 8, marginBottom: 10, width: '100%' }}
                >
                  <option value="">— Select device that passed —</option>
                  {DEVICE_TYPES.map((dt) => (
                    <option key={dt.key} value={dt.key}>{dt.label}</option>
                  ))}
                </select>
                <button
                  data-testid="reinspect-complete-btn"
                  onClick={handleCompleteReinspection}
                  disabled={reinspecting || !reinspectCompleteDevice}
                  style={{ padding: '8px 16px', background: '#15803d', color: '#fff', borderRadius: 4 }}
                >
                  {reinspecting ? 'Updating…' : 'Device Passed Re-Inspection'}
                </button>
              </div>

              {reinspectMsg && (
                <div data-testid="reinspect-msg" style={{ padding: 10, borderRadius: 6,
                  background: reinspectMsg.startsWith('✅') ? '#f0fdf4' : '#fef2f2' }}>
                  {reinspectMsg}
                </div>
              )}
            </div>
          )}

          {/* ── TAB 4: Tenant Termination Workflow (T-114-003) ───────────── */}
          {activeTab === 'termination' && (
            <div data-testid="tab-panel-termination">
              <p style={{ marginBottom: 12 }}>
                If the 7-day cure window has expired without all devices being installed,
                the landlord must issue the tenant's right-to-terminate notice per Texas Property Code § 92.156.
              </p>

              {/* Cure status summary */}
              {cureStatus && (
                <div data-testid="cure-status-panel" style={{ marginBottom: 16, padding: 14,
                  background: cureStatus.cureFailed ? '#fef2f2' : cureStatus.cureWindowActive ? '#fffbeb' : '#f0fdf4',
                  border: `1px solid ${cureStatus.cureFailed ? '#fca5a5' : cureStatus.cureWindowActive ? '#fbbf24' : '#86efac'}`,
                  borderRadius: 6 }}>
                  <p><strong>Cure window active:</strong> {cureStatus.cureWindowActive ? 'Yes' : 'No'}</p>
                  {cureStatus.expiresAt && <p><strong>Expires:</strong> {new Date(cureStatus.expiresAt).toLocaleDateString()}</p>}
                  {cureStatus.daysRemaining !== null && <p data-testid="days-remaining"><strong>Days remaining:</strong> {cureStatus.daysRemaining}</p>}
                  <p><strong>Cure failed:</strong> {cureStatus.cureFailed ? '⚠️ Yes — termination notice required' : 'No'}</p>
                  <p><strong>Termination notice issued:</strong> {cureStatus.terminationNoticeIssued ? '✅ Yes' : 'No'}</p>
                </div>
              )}

              {/* Termination issue form */}
              <div style={{ padding: 14, background: '#fff8f8', border: '1px solid #fca5a5', borderRadius: 6 }}>
                <h3 style={{ marginBottom: 10, color: '#991b1b' }}>Issue Tenant Right-to-Terminate Notice</h3>
                <p style={{ fontSize: 13, marginBottom: 10 }}>
                  Statutory basis: Texas Property Code § 92.156. This notice is only authorized when the 7-day cure window has been breached.
                </p>
                <div style={{ marginBottom: 10 }}>
                  <label htmlFor="tenant-id" style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                    Tenant ID
                  </label>
                  <input
                    id="tenant-id"
                    type="text"
                    data-testid="tenant-id-input"
                    value={tenantId}
                    onChange={(e) => setTenantId(e.target.value)}
                    placeholder="Tenant identifier"
                    style={{ width: '100%', padding: 8 }}
                  />
                </div>
                <button
                  data-testid="issue-termination-btn"
                  onClick={handleIssueTermination}
                  disabled={issuing || !tenantId.trim()}
                  style={{ padding: '8px 16px', background: '#dc2626', color: '#fff', borderRadius: 4 }}
                >
                  {issuing ? 'Issuing…' : 'Issue Termination Notice'}
                </button>
              </div>

              {terminationMsg && (
                <div data-testid="termination-msg" style={{ marginTop: 12, padding: 10, borderRadius: 6,
                  background: terminationMsg.startsWith('✅') ? '#f0fdf4' : '#fef2f2' }}>
                  {terminationMsg}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SubchapterDChecklist;
